import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, FiberManualRecord as DotIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Renders a collapsible "how to use" guide. `sections` items with a
// `featureKey` are hidden when that feature is disabled for the school —
// same "missing row = enabled" rule used everywhere else (AuthContext.isFeatureEnabled).
const HelpGuideLayout = ({ title, subtitle, sections }) => {
  const { isFeatureEnabled } = useAuth();

  const visibleSections = sections.filter((s) => !s.featureKey || isFeatureEnabled(s.featureKey));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 920, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>
      )}

      {visibleSections.map((section, index) => {
        const Icon = section.icon;
        return (
          <Accordion
            key={section.id}
            defaultExpanded={index === 0}
            sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {Icon && <Icon color="primary" fontSize="small" />}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {section.content.map((block, i) => (
                <Box key={i} sx={{ mb: i < section.content.length - 1 ? 2 : 0 }}>
                  {block.heading && (
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {block.heading}
                    </Typography>
                  )}
                  <List dense disablePadding>
                    {block.points.map((point, j) => (
                      <ListItem key={j} disablePadding sx={{ alignItems: 'flex-start', py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 22, mt: 0.7 }}>
                          <DotIcon sx={{ fontSize: 7 }} />
                        </ListItemIcon>
                        <ListItemText primary={point} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default HelpGuideLayout;
